import {Injectable, signal} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable, forkJoin, of, throwError} from 'rxjs';
import {catchError, finalize, map, take, tap} from 'rxjs/operators';

import {environment} from '@/environments';
import {BaseResponse, Page, SelectResponse} from '@/shared/models';
import {HasActions} from '@/shared/ui/data-grid/data-grid.component';
import {SecureStorage} from '@/core/storage/secure-storage';
import {ApiFeedbackService} from '@/core/api-feedback.service';

export interface SalesClientRow extends HasActions {
  uuid: string;
  displayName: string;
  paternalLastName: string;
  maternalLastName: string;
  curp: string;
  rfc: string;
  clientCode: string;
  type: string;
  classification: string;
  cellphone: string;
  email: string;
  units: string;
  unitsCount: number;
  status: string;
  identityStatus: string;
  identityStatusLabel: string;
  actions: HasActions['actions'];
  isEnable: boolean;
  avatar: {url?: string | null} | null;
}

interface SalesClientDto {
  uuid?: string | null;
  displayName?: string | null;
  paternalLastName?: string | null;
  maternalLastName?: string | null;
  curp?: string | null;
  rfc?: string | null;
  clientCode?: string | null;
  type?: string | null;
  classification?: string | null;
  cellphone?: string | null;
  email?: string | null;
  units?: string | number | null;
  unitsCount?: number | null;
  status?: string | null;
  identityStatus?: string | null;
  identityStatusLabel?: string | null;
  avatar?: {url?: string | null} | null;
  actions?: Partial<Record<string, boolean | null>> | null;
}

interface CustomerCreateResponse {
  uuid?: string | null;
}

export type CustomerIdentificationOption = SelectResponse & {
  value?: {
    identifier?: string;
    requiresFront?: boolean;
    requiresBack?: boolean;
  } | null;
};

export interface IdentificationFileTypeOption extends SelectResponse {
  prefix?: string | null;
}

export interface SalesClientDetail {
  uuid: string;
  type: SelectResponse | null;
  classification: SelectResponse | null;
  gender: SelectResponse | null;
  identificationType: CustomerIdentificationOption | null;
  identificationFileType: IdentificationFileTypeOption | null;
  companyType: SelectResponse | null;
  clientCode: string | null;
  curp: string | null;
  rfc: string | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  secondLastName: string | null;
  businessName: string | null;
  identificationNumber: string | null;
  cellphoneLada?: string | null;
  cellphone: string | null;
  phoneLada?: string | null;
  phone: string | null;
  email: string | null;
  birthDate: string | null;
  employmentPlace: string | null;
  socialSecurityNumber: string | null;
  notes: string | null;
  avatarUrl: string | null;
  identificationFrontUrl: string | null;
  identificationBackUrl: string | null;
  identificationFrontName?: string | null;
  identificationBackName?: string | null;
  fiscalCertificateUrl?: string | null;
  enable: boolean;
}

export interface CustomerAddressRow extends HasActions {
  uuid: string;
  street: string;
  exteriorNumber: string;
  interiorNumber?: string | null;
  colony: string;
  delegation: string;
  municipality: string;
  state: string;
  country: string;
  zipCode: string;
  isPrimary: boolean;
}

export interface CustomerAddressDetail {
  uuid: string;
  address: CustomerAddressPayload['address'];
}

export interface CustomerAddressPayload {
  address: {
    street: string;
    exteriorNumber: string;
    interiorNumber?: string | null;
    zipCode: string;
    delegation?: string | null;
    colony: string;
    country: {uuid: string; label?: string | null};
    state: {uuid: string; label?: string | null};
    municipality: {uuid: string; label?: string | null};
  };
}

export interface CustomerReferenceRow extends HasActions {
  uuid: string;
  fullName: string;
  email: string;
  phone: string;
  identityStatus: string;
  identityStatusLabel: string;
}

export interface CustomerReferenceDetail {
  uuid: string;
  fullName: string;
  email: string;
  phone: string;
}

export interface CustomerReferencePayload {
  fullName: string;
  email: string;
  phone: string;
}

export interface CustomerCoOwnerRow extends CustomerReferenceRow {}
export interface CustomerCoOwnerDetail extends CustomerReferenceDetail {}
export interface CustomerCoOwnerPayload extends CustomerReferencePayload {}

export interface CustomerAdditionalInfoDetail {
  uuid: string;
  nationality: string | null;
  birthPlace: string | null;
  maritalStatus: SelectResponse | null;
  maritalRegime: SelectResponse | null;
  educationLevel: SelectResponse | null;
  monthlyIncome: number | null;
  employerBusinessName: string | null;
  employerAddress: string | null;
  employerCountry: SelectResponse | null;
  employerState: SelectResponse | null;
  employerMunicipality: SelectResponse | null;
  employerOfficePhone: string | null;
  employerOfficePhoneExt: string | null;
  employerRegistry: string | null;
  employerEconomyType: SelectResponse | null;
  spouseFullName: string | null;
  spouseNationality: string | null;
  spouseRfc: string | null;
  spouseBirthPlace: string | null;
  spouseBirthDate: string | null;
  spouseEmail: string | null;
  spousePhone: string | null;
  spouseOccupation: SelectResponse | null;
  spouseMonthlyIncome: number | null;
}

export interface CustomerAdditionalInfoPayload {
  nationality?: string | null;
  birthPlace?: string | null;
  maritalStatusUuid?: string | null;
  maritalRegimeUuid?: string | null;
  educationLevelUuid?: string | null;
  monthlyIncome?: number | null;
  employerBusinessName?: string | null;
  employerAddress?: string | null;
  employerCountryUuid?: string | null;
  employerStateUuid?: string | null;
  employerMunicipalityUuid?: string | null;
  employerOfficePhone?: string | null;
  employerOfficePhoneExt?: string | null;
  employerRegistry?: string | null;
  employerEconomyTypeUuid?: string | null;
  spouseFullName?: string | null;
  spouseNationality?: string | null;
  spouseRfc?: string | null;
  spouseBirthPlace?: string | null;
  spouseBirthDate?: string | null;
  spouseEmail?: string | null;
  spousePhone?: string | null;
  spouseOccupationUuid?: string | null;
  spouseMonthlyIncome?: number | null;
}

export interface CustomerAdditionalInfoCatalogs {
  maritalStatuses: SelectResponse[];
  maritalRegimes: SelectResponse[];
  educationLevels: SelectResponse[];
  occupations: SelectResponse[];
  economyTypes: SelectResponse[];
}

export interface CustomerFormPayload {
  typeUuid: string;
  classificationUuid: string;
  genderUuid?: string | null;
  identificationTypeUuid?: string | null;
  identificationFileTypeUuid?: string | null;
  companyTypeUuid?: string | null;
  curp?: string | null;
  rfc?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  secondLastName?: string | null;
  businessName?: string | null;
  identificationNumber?: string | null;
  cellphoneLada?: string | null;
  cellphone?: string | null;
  phoneLada?: string | null;
  phone?: string | null;
  email?: string | null;
  birthDate?: string | null;
  employmentPlace?: string | null;
  socialSecurityNumber?: string | null;
  notes?: string | null;
  avatar?: File | null;
  identificationFront?: File | null;
  identificationBack?: File | null;
  fiscalCertificate?: File | null;
  clientCode?: string | null;
}

@Injectable({providedIn: 'root'})
export class RealEstateCustomersService {
  private readonly pageSignal = signal<Page<SalesClientRow> | null>(null);
  private readonly loadingSignal = signal(false);

  readonly page = this.pageSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();

  constructor(
    private http: HttpClient,
    private storage: SecureStorage,
    private feedback: ApiFeedbackService
  ) {}

  private base(): string {
    return `${environment.apiBase}/real-estate/customers`;
  }

  getNextCode(): Observable<number> {
    return this.nextId();
  }

  getCustomer(uuid: string): Observable<SalesClientDetail> {
    return this.get(uuid);
  }

  getDevelopments(): Observable<SelectResponse[]> {
    return this.http
      .get<BaseResponse<SelectResponse[]>>(`${environment.apiBase}/models/filters/developments`, {
        headers: this.authHeaders()
      })
      .pipe(
        take(1),
        map(res => res.data ?? []),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al cargar desarrollos';
          this.feedback.showError(msg);
          return throwError(() => err);
        })
      );
  }

  getIdentificationFileTypes(documentType: string = 'IDENTIFICATION'): Observable<IdentificationFileTypeOption[]> {
    const headers = this.authHeaders();
    const params = new HttpParams().set('documentType', documentType);
    return this.http
      .get<BaseResponse<Array<{uuid: string; name: string; prefix?: string | null}>>>(
        `${environment.apiBase}/general/select/file-types`,
        {headers, params}
      )
      .pipe(
        take(1),
        map(res => (res.data ?? []).map(item => ({
          uuid: item.uuid,
          name: item.name,
          value: item.prefix ?? null,
          prefix: (item.prefix ?? '').toUpperCase()
        } as IdentificationFileTypeOption))),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al cargar tipos de archivo';
          this.feedback.showError(msg);
          return throwError(() => err);
        })
      );
  }

  load(
    page = 0,
    size = 10,
    keyword = '',
    sortBy = 'createdAt',
    order: '' | 'asc' | 'desc' = 'desc',
    developments: string[] = [],
    statuses: string[] = [],
    types: string[] = [],
    classifications: string[] = [],
    identityStatusLabels: string[] = [],
    columnFilters: Record<string, string> = {}
  ): void {
    this.loadingSignal.set(true);

    const params = this.buildCustomerListParams(
      page,
      size,
      keyword,
      sortBy,
      order,
      developments,
      statuses,
      types,
      classifications,
      identityStatusLabels,
      columnFilters
    );

    this.http
      .get<BaseResponse<Page<SalesClientDto>>>(this.base(), {params, headers: this.authHeaders()})
      .pipe(
        map(res => this.mapPage(res.data)),
        tap(mapped => this.pageSignal.set(mapped)),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al cargar clientes';
          this.feedback.showError(msg);
          return throwError(() => err);
        }),
        finalize(() => this.loadingSignal.set(false))
      )
      .subscribe();
  }

  loadAppend(
    page = 0,
    size = 10,
    keyword = '',
    sortBy = 'createdAt',
    order: '' | 'asc' | 'desc' = 'desc',
    developments: string[] = [],
    statuses: string[] = [],
    types: string[] = [],
    classifications: string[] = [],
    identityStatusLabels: string[] = [],
    columnFilters: Record<string, string> = {}
  ): void {
    this.loadingSignal.set(true);

    const params = this.buildCustomerListParams(
      page,
      size,
      keyword,
      sortBy,
      order,
      developments,
      statuses,
      types,
      classifications,
      identityStatusLabels,
      columnFilters
    );

    this.http
      .get<BaseResponse<Page<SalesClientDto>>>(this.base(), {params, headers: this.authHeaders()})
      .pipe(
        map(res => this.mapPage(res.data)),
        tap(mapped => {
          const current = this.pageSignal();
          if (!current || page === 0) {
            this.pageSignal.set(mapped);
            return;
          }
          this.pageSignal.set({
            ...mapped,
            content: [...(current.content ?? []), ...(mapped.content ?? [])]
          });
        }),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al cargar clientes';
          this.feedback.showError(msg);
          return throwError(() => err);
        }),
        finalize(() => this.loadingSignal.set(false))
      )
      .subscribe();
  }

  get(uuid: string): Observable<SalesClientDetail> {
    if (!uuid) {
      return throwError(() => new Error('Cliente requerido'));
    }

    return this.http
      .get<BaseResponse<any>>(`${this.base()}/${uuid}`, {
        headers: this.authHeaders()
      })
      .pipe(
        take(1),
        map(res => this.mapDetail(res.data ?? {})),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al cargar el cliente';
          this.feedback.showError(msg);
          return throwError(() => err);
        })
      );
  }

  nextId(): Observable<number> {
    return this.http
      .get<BaseResponse<number>>(`${this.base()}/next-id`, {
        headers: this.authHeaders()
      })
      .pipe(
        take(1),
        map(res => res.data ?? 0),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al obtener siguiente ID';
          this.feedback.showError(msg);
          return throwError(() => err);
        })
      );
  }

  create(body: CustomerFormPayload): Observable<string> {
    this.loadingSignal.set(true);
    this.feedback.showLoading('Guardando cliente…');
    const form = this.buildFormData(body);

    return this.http
      .post<BaseResponse<CustomerCreateResponse>>(this.base(), form, {headers: this.authHeaders()})
      .pipe(
        map(res => {
          const uuid = res.data?.uuid;
          if (!uuid) {
            throw new Error('No se recibió el uuid del cliente creado');
          }
          return uuid;
        }),
        tap(() => this.feedback.showSuccess('Cliente guardado correctamente')),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? err.message ?? 'Error al guardar cliente';
          this.feedback.showError(msg);
          return throwError(() => err);
        }),
        finalize(() => this.loadingSignal.set(false))
      );
  }

  update(uuid: string, body: CustomerFormPayload): Observable<void> {
    if (!uuid) {
      return throwError(() => new Error('Cliente requerido'));
    }
    this.loadingSignal.set(true);
    this.feedback.showLoading('Actualizando cliente…');
    const form = this.buildFormData(body);

    return this.http
      .put<BaseResponse<void>>(`${this.base()}/${uuid}`, form, {headers: this.authHeaders()})
      .pipe(
        map(() => void 0),
        tap(() => this.feedback.showSuccess('Cliente actualizado correctamente')),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al actualizar cliente';
          this.feedback.showError(msg);
          return throwError(() => err);
        }),
        finalize(() => this.loadingSignal.set(false))
      );
  }

  toggleStatus(uuid: string): Observable<void> {
    if (!uuid) {
      return throwError(() => new Error('Cliente requerido'));
    }
    this.loadingSignal.set(true);
    this.feedback.showLoading('Actualizando estatus…');

    return this.http
      .patch<BaseResponse<void>>(`${this.base()}/${uuid}/status`, {}, {headers: this.authHeaders()})
      .pipe(
        map(() => void 0),
        tap(() => this.feedback.showSuccess('Estatus actualizado correctamente')),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al actualizar estatus';
          this.feedback.showError(msg);
          return throwError(() => err);
        }),
        finalize(() => this.loadingSignal.set(false))
      );
  }

  delete(uuid: string): Observable<void> {
    if (!uuid) {
      return throwError(() => new Error('Cliente requerido'));
    }
    this.loadingSignal.set(true);
    this.feedback.showLoading('Eliminando cliente…');

    return this.http
      .delete<BaseResponse<void>>(`${this.base()}/${uuid}`, {headers: this.authHeaders()})
      .pipe(
        map(() => void 0),
        tap(() => this.feedback.showSuccess('Cliente eliminado correctamente')),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al eliminar cliente';
          this.feedback.showError(msg);
          return throwError(() => err);
        }),
        finalize(() => this.loadingSignal.set(false))
      );
  }

  // Addresses
  listAddresses(customerUuid: string): Observable<CustomerAddressRow[]> {
    return this.http
      .get<BaseResponse<CustomerAddressRow[]>>(
        `${this.base()}/${customerUuid}/addresses`,
        {headers: this.authHeaders()}
      )
      .pipe(
        take(1),
        map(res => res.data ?? []),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al cargar domicilios';
          this.feedback.showError(msg);
          return throwError(() => err);
        })
      );
  }

  getAddress(customerUuid: string, addressUuid: string): Observable<CustomerAddressDetail> {
    return this.http
      .get<BaseResponse<CustomerAddressDetail>>(
        `${this.base()}/${customerUuid}/addresses/${addressUuid}`,
        {headers: this.authHeaders()}
      )
      .pipe(
        take(1),
        map(res => res.data as CustomerAddressDetail),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al cargar domicilio';
          this.feedback.showError(msg);
          return throwError(() => err);
        })
      );
  }

  createAddress(customerUuid: string, payload: CustomerAddressPayload): Observable<void> {
    return this.http
      .post<BaseResponse<void>>(
        `${this.base()}/${customerUuid}/addresses`,
        payload,
        {headers: this.authHeaders()}
      )
      .pipe(
        map(() => void 0),
        tap(() => this.feedback.showSuccess('Domicilio guardado correctamente')),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al guardar domicilio';
          this.feedback.showError(msg);
          return throwError(() => err);
        })
      );
  }

  updateAddress(customerUuid: string, addressUuid: string, payload: CustomerAddressPayload): Observable<void> {
    return this.http
      .put<BaseResponse<void>>(
        `${this.base()}/${customerUuid}/addresses/${addressUuid}`,
        payload,
        {headers: this.authHeaders()}
      )
      .pipe(
        map(() => void 0),
        tap(() => this.feedback.showSuccess('Domicilio actualizado correctamente')),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al actualizar domicilio';
          this.feedback.showError(msg);
          return throwError(() => err);
        })
      );
  }

  deleteAddress(customerUuid: string, addressUuid: string): Observable<void> {
    return this.http
      .delete<BaseResponse<void>>(
        `${this.base()}/${customerUuid}/addresses/${addressUuid}`,
        {headers: this.authHeaders()}
      )
      .pipe(
        map(() => void 0),
        tap(() => this.feedback.showSuccess('Domicilio eliminado correctamente')),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al eliminar domicilio';
          this.feedback.showError(msg);
          return throwError(() => err);
        })
      );
  }

  // References
  listReferences(customerUuid: string): Observable<CustomerReferenceRow[]> {
    return this.http
      .get<BaseResponse<CustomerReferenceRow[]>>(
        `${this.base()}/${customerUuid}/references`,
        {headers: this.authHeaders()}
      )
      .pipe(
        take(1),
        map(res => res.data ?? []),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al cargar referencias';
          this.feedback.showError(msg);
          return throwError(() => err);
        })
      );
  }

  getReference(customerUuid: string, referenceUuid: string): Observable<CustomerReferenceDetail> {
    return this.http
      .get<BaseResponse<CustomerReferenceDetail>>(
        `${this.base()}/${customerUuid}/references/${referenceUuid}`,
        {headers: this.authHeaders()}
      )
      .pipe(
        take(1),
        map(res => res.data as CustomerReferenceDetail),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al cargar referencia';
          this.feedback.showError(msg);
          return throwError(() => err);
        })
      );
  }

  createReference(customerUuid: string, payload: CustomerReferencePayload): Observable<void> {
    return this.http
      .post<BaseResponse<void>>(
        `${this.base()}/${customerUuid}/references`,
        payload,
        {headers: this.authHeaders()}
      )
      .pipe(
        map(() => void 0),
        tap(() => this.feedback.showSuccess('Referencia guardada correctamente')),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al guardar referencia';
          this.feedback.showError(msg);
          return throwError(() => err);
        })
      );
  }

  updateReference(customerUuid: string, referenceUuid: string, payload: CustomerReferencePayload): Observable<void> {
    return this.http
      .put<BaseResponse<void>>(
        `${this.base()}/${customerUuid}/references/${referenceUuid}`,
        payload,
        {headers: this.authHeaders()}
      )
      .pipe(
        map(() => void 0),
        tap(() => this.feedback.showSuccess('Referencia actualizada correctamente')),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al actualizar referencia';
          this.feedback.showError(msg);
          return throwError(() => err);
        })
      );
  }

  deleteReference(customerUuid: string, referenceUuid: string): Observable<void> {
    return this.http
      .delete<BaseResponse<void>>(
        `${this.base()}/${customerUuid}/references/${referenceUuid}`,
        {headers: this.authHeaders()}
      )
      .pipe(
        map(() => void 0),
        tap(() => this.feedback.showSuccess('Referencia eliminada correctamente')),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al eliminar referencia';
          this.feedback.showError(msg);
          return throwError(() => err);
        })
      );
  }

  // Co-owners
  listCoOwners(customerUuid: string): Observable<CustomerCoOwnerRow[]> {
    return this.http
      .get<BaseResponse<CustomerCoOwnerRow[]>>(
        `${this.base()}/${customerUuid}/co-owners`,
        {headers: this.authHeaders()}
      )
      .pipe(
        take(1),
        map(res => res.data ?? []),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al cargar copropietarios';
          this.feedback.showError(msg);
          return throwError(() => err);
        })
      );
  }

  getCoOwner(customerUuid: string, coOwnerUuid: string): Observable<CustomerCoOwnerDetail> {
    return this.http
      .get<BaseResponse<CustomerCoOwnerDetail>>(
        `${this.base()}/${customerUuid}/co-owners/${coOwnerUuid}`,
        {headers: this.authHeaders()}
      )
      .pipe(
        take(1),
        map(res => res.data as CustomerCoOwnerDetail),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al cargar copropietario';
          this.feedback.showError(msg);
          return throwError(() => err);
        })
      );
  }

  createCoOwner(customerUuid: string, payload: CustomerCoOwnerPayload): Observable<void> {
    return this.http
      .post<BaseResponse<void>>(
        `${this.base()}/${customerUuid}/co-owners`,
        payload,
        {headers: this.authHeaders()}
      )
      .pipe(
        map(() => void 0),
        tap(() => this.feedback.showSuccess('Copropietario guardado correctamente')),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al guardar copropietario';
          this.feedback.showError(msg);
          return throwError(() => err);
        })
      );
  }

  updateCoOwner(customerUuid: string, coOwnerUuid: string, payload: CustomerCoOwnerPayload): Observable<void> {
    return this.http
      .put<BaseResponse<void>>(
        `${this.base()}/${customerUuid}/co-owners/${coOwnerUuid}`,
        payload,
        {headers: this.authHeaders()}
      )
      .pipe(
        map(() => void 0),
        tap(() => this.feedback.showSuccess('Copropietario actualizado correctamente')),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al actualizar copropietario';
          this.feedback.showError(msg);
          return throwError(() => err);
        })
      );
  }

  deleteCoOwner(customerUuid: string, coOwnerUuid: string): Observable<void> {
    return this.http
      .delete<BaseResponse<void>>(
        `${this.base()}/${customerUuid}/co-owners/${coOwnerUuid}`,
        {headers: this.authHeaders()}
      )
      .pipe(
        map(() => void 0),
        tap(() => this.feedback.showSuccess('Copropietario eliminado correctamente')),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al eliminar copropietario';
          this.feedback.showError(msg);
          return throwError(() => err);
        })
      );
  }

  getAdditionalInfo(customerUuid: string): Observable<CustomerAdditionalInfoDetail | null> {
    if (!customerUuid) {
      return throwError(() => new Error('Cliente requerido'));
    }

    return this.http
      .get<BaseResponse<any>>(
        `${this.base()}/${customerUuid}/additional-info`,
        {headers: this.authHeaders()}
      )
      .pipe(
        take(1),
        map(res => this.mapAdditionalInfoDetail(res.data ?? null)),
        catchError(err => {
          if (err.status === 404) {
            return of(null);
          }
          const msg = err.error?.error ?? err.error?.message ?? 'Error al cargar información adicional';
          this.feedback.showError(msg);
          return throwError(() => err);
        })
      );
  }

  createAdditionalInfo(customerUuid: string, payload: CustomerAdditionalInfoPayload): Observable<void> {
    if (!customerUuid) {
      return throwError(() => new Error('Cliente requerido'));
    }

    this.feedback.showLoading('Guardando información adicional…');
    return this.http
      .post<BaseResponse<void>>(
        `${this.base()}/${customerUuid}/additional-info`,
        payload,
        {headers: this.authHeaders()}
      )
      .pipe(
        map(() => void 0),
        tap(() => this.feedback.showSuccess('Información adicional guardada correctamente')),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al guardar información adicional';
          this.feedback.showError(msg);
          return throwError(() => err);
        }),
        finalize(() => this.feedback.hide())
      );
  }

  updateAdditionalInfo(customerUuid: string, payload: CustomerAdditionalInfoPayload): Observable<void> {
    if (!customerUuid) {
      return throwError(() => new Error('Cliente requerido'));
    }

    this.feedback.showLoading('Actualizando información adicional…');
    return this.http
      .put<BaseResponse<void>>(
        `${this.base()}/${customerUuid}/additional-info`,
        payload,
        {headers: this.authHeaders()}
      )
      .pipe(
        map(() => void 0),
        tap(() => this.feedback.showSuccess('Información adicional actualizada correctamente')),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al actualizar información adicional';
          this.feedback.showError(msg);
          return throwError(() => err);
        }),
        finalize(() => this.feedback.hide())
      );
  }

  exportExcel(): Observable<Blob> {
    this.feedback.showLoading('Descargando Excel...');
    return this.http
      .get(`${this.base()}/excel`, {
        headers: this.authHeaders(),
        responseType: 'blob',
        observe: 'response'
      })
      .pipe(
        take(1),
        tap(response => {
          const contentDisposition = response.headers.get('Content-Disposition');
          const fileName = contentDisposition
            ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') || 'clientes.xlsx'
            : 'clientes.xlsx';

          const blob = response.body ?? new Blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          link.click();
          window.URL.revokeObjectURL(url);

          this.feedback.showSuccess('Excel exportado correctamente');
        }),
        map(response => response.body ?? new Blob()),
        catchError(err => {
          const msg = err.error?.error ?? err.error?.message ?? 'Error al exportar clientes';
          this.feedback.showError(msg);
          return throwError(() => err);
        })
      );
  }

  getCatalogs(): Observable<{
    types: SelectResponse[];
    classifications: SelectResponse[];
    genders: SelectResponse[];
    identificationTypes: CustomerIdentificationOption[];
    companyTypes: SelectResponse[];
    identificationFileTypes: IdentificationFileTypeOption[];
  }> {
    const headers = this.authHeaders();
    return forkJoin({
      types: this.http.get<BaseResponse<SelectResponse[]>>(`${environment.apiBase}/general/select/customer-types`, {headers}),
      classifications: this.http.get<BaseResponse<SelectResponse[]>>(`${environment.apiBase}/general/select/customer-classifications`, {headers}),
      genders: this.http.get<BaseResponse<SelectResponse[]>>(`${environment.apiBase}/general/select/genders`, {headers}),
      identificationTypes: this.http.get<BaseResponse<CustomerIdentificationOption[]>>(
        `${environment.apiBase}/general/select/customer-identification-types`,
        {headers}
      ),
      companyTypes: this.http.get<BaseResponse<SelectResponse[]>>(`${environment.apiBase}/general/select/customer-company-types`, {headers}),
      identificationFileTypes: this.http.get<BaseResponse<Array<{uuid: string; name: string; prefix?: string | null}>>>(
        `${environment.apiBase}/general/select/file-types`,
        {
          headers,
          params: new HttpParams().set('documentType', 'IDENTIFICATION')
        }
      )
    }).pipe(
      take(1),
      map(res => ({
        types: res.types.data ?? [],
        classifications: res.classifications.data ?? [],
        genders: res.genders.data ?? [],
        identificationTypes: (res.identificationTypes.data ?? []).map(opt => ({
          ...opt,
          value: opt.value ?? null
        })),
        companyTypes: res.companyTypes.data ?? [],
        identificationFileTypes: (res.identificationFileTypes.data ?? []).map(item => ({
          uuid: item.uuid,
          name: item.name,
          value: item.prefix ?? null,
          prefix: (item.prefix ?? '').toUpperCase()
        }))
      })),
      catchError(err => {
        const msg = err.error?.error ?? err.error?.message ?? 'Error al cargar catálogos';
        this.feedback.showError(msg);
        return throwError(() => err);
      })
    );
  }

  getAdditionalInfoCatalogs(): Observable<CustomerAdditionalInfoCatalogs> {
    const headers = this.authHeaders();
    return forkJoin({
      maritalStatuses: this.http.get<BaseResponse<SelectResponse[]>>(
        `${environment.apiBase}/general/select/customer-marital-statuses`,
        {headers}
      ),
      maritalRegimes: this.http.get<BaseResponse<SelectResponse[]>>(
        `${environment.apiBase}/general/select/customer-marital-regimes`,
        {headers}
      ),
      educationLevels: this.http.get<BaseResponse<SelectResponse[]>>(
        `${environment.apiBase}/general/select/customer-education-levels`,
        {headers}
      ),
      occupations: this.http.get<BaseResponse<SelectResponse[]>>(
        `${environment.apiBase}/general/select/customer-occupations`,
        {headers}
      ),
      economyTypes: this.http.get<BaseResponse<SelectResponse[]>>(
        `${environment.apiBase}/general/select/customer-economy-types`,
        {headers}
      )
    }).pipe(
      take(1),
      map(res => ({
        maritalStatuses: res.maritalStatuses.data ?? [],
        maritalRegimes: res.maritalRegimes.data ?? [],
        educationLevels: res.educationLevels.data ?? [],
        occupations: res.occupations.data ?? [],
        economyTypes: res.economyTypes.data ?? []
      })),
      catchError(err => {
        const msg = err.error?.error ?? err.error?.message ?? 'Error al cargar catálogos';
        this.feedback.showError(msg);
        return throwError(() => err);
      })
    );
  }

  private mapPage(page: Page<SalesClientDto> | null): Page<SalesClientRow> {
    if (!page) {
      return {
        content: [],
        pageable: {pageNumber: 0, pageSize: 0, offset: 0},
        totalPages: 0,
        totalElements: 0,
        first: true,
        last: true,
        size: 0,
        number: 0,
        numberOfElements: 0,
        empty: true
      };
    }

    return {
      ...page,
      content: (page.content ?? []).map(item => this.mapRow(item))
    };
  }

  private mapRow(dto: SalesClientDto): SalesClientRow {
    return {
      uuid: dto.uuid ?? this.uuid(),
      displayName: dto.displayName ?? '—',
      paternalLastName: dto.paternalLastName ?? '—',
      maternalLastName: dto.maternalLastName ?? '—',
      curp: dto.curp ?? '—',
      rfc: dto.rfc ?? '—',
      clientCode: dto.clientCode ?? '—',
      type: dto.type ?? '—',
      classification: dto.classification ?? '—',
      cellphone: dto.cellphone ?? '—',
      email: dto.email ?? '—',
      units: this.normalizeUnits(dto.units),
      unitsCount: dto.unitsCount ?? 0,
      status: dto.status ?? '—',
      identityStatus: dto.identityStatus ?? 'PENDING',
      identityStatusLabel: dto.identityStatusLabel ?? 'Sin verificar',
      avatar: dto.avatar ?? null,
      isEnable: dto.status?.toLowerCase?.() === 'activo',
      actions: this.mapActions(dto.actions)
    };
  }

  private normalizeUnits(units: SalesClientDto['units']): string {
    if (units == null) return '—';
    if (typeof units === 'number') return String(units);
    const normalized = units.replace(/\r\n?/g, '\n').trim();
    return normalized || '—';
  }

  private mapActions(actions?: Partial<Record<string, boolean | null>> | null): HasActions['actions'] {
    return {
      show: actions?.['show'] ?? false,
      edit: actions?.['edit'] ?? false,
      delete: actions?.['delete'] ?? false,
      pdf: actions?.['pdf'] ?? false,
      reservation: actions?.['reservation'] ?? false,
      depositRequest: actions?.['depositRequest'] ?? false,
      deposits: actions?.['deposits'] ?? false,
      blocked: actions?.['blocked'] ?? false,
      finish: actions?.['finish'] ?? false,
      status: actions?.['status'] ?? false,
      document: actions?.['document'] ?? false,
      images: actions?.['images'] ?? false
    };
  }

  private mapDetail(data: any): SalesClientDetail {
    const identificationType = data.identificationType
      ? {
          uuid: data.identificationType.uuid ?? null,
          name: data.identificationType.name ?? data.identificationType.value?.name ?? '',
          value: data.identificationType.value ?? null
        } as CustomerIdentificationOption
      : null;
    const identificationFileType = data.identificationFileType
      ? {
          uuid: data.identificationFileType.uuid ?? null,
          name: data.identificationFileType.name ?? '',
          value: data.identificationFileType.prefix ?? null,
          prefix: data.identificationFileType.prefix ?? null
        } as IdentificationFileTypeOption
      : null;
    return {
      uuid: data.uuid ?? this.uuid(),
      type: data.type ?? null,
      classification: data.classification ?? null,
      gender: data.gender ?? null,
      identificationType,
      identificationFileType,
      companyType: data.companyType ?? null,
      clientCode: data.clientCode ?? null,
      curp: data.curp ?? null,
      rfc: data.rfc ?? null,
      firstName: data.firstName ?? null,
      middleName: data.middleName ?? null,
      lastName: data.lastName ?? null,
      secondLastName: data.secondLastName ?? null,
      businessName: data.businessName ?? null,
      identificationNumber: data.identificationNumber ?? null,
      cellphoneLada: data.cellphoneLada ?? null,
      cellphone: data.cellphone ?? null,
      phoneLada: data.phoneLada ?? null,
      phone: data.phone ?? null,
      email: data.email ?? null,
      birthDate: data.birthDate ?? null,
      employmentPlace: data.employmentPlace ?? null,
      socialSecurityNumber: data.socialSecurityNumber ?? null,
      notes: data.notes ?? null,
      avatarUrl: data.avatar?.url ?? null,
      identificationFrontUrl: data.identificationFront?.url ?? null,
      identificationBackUrl: data.identificationBack?.url ?? null,
      identificationFrontName: data.identificationFront?.name ?? null,
      identificationBackName: data.identificationBack?.name ?? null,
      fiscalCertificateUrl: data.fiscalCertificate?.url ?? null,
      enable: Boolean(data.enable ?? data.isEnable ?? true)
    };
  }

  private mapAdditionalInfoDetail(dto: any | null): CustomerAdditionalInfoDetail | null {
    if (!dto) {
      return null;
    }

    const toSelect = (item: any): SelectResponse | null =>
      item ? {uuid: item.uuid ?? '', name: item.name ?? item.label ?? ''} : null;

    return {
      uuid: dto?.uuid ?? '',
      nationality: dto?.nationality ?? null,
      birthPlace: dto?.birthPlace ?? null,
      maritalStatus: toSelect(dto?.maritalStatus),
      maritalRegime: toSelect(dto?.maritalRegime),
      educationLevel: toSelect(dto?.educationLevel),
      monthlyIncome: dto?.monthlyIncome ?? null,
      employerBusinessName: dto?.employerBusinessName ?? null,
      employerAddress: dto?.employerAddress ?? null,
      employerCountry: toSelect(dto?.employerCountry),
      employerState: toSelect(dto?.employerState),
      employerMunicipality: toSelect(dto?.employerMunicipality),
      employerOfficePhone: dto?.employerOfficePhone ?? null,
      employerOfficePhoneExt: dto?.employerOfficePhoneExt ?? null,
      employerRegistry: dto?.employerRegistry ?? null,
      employerEconomyType: toSelect(dto?.employerEconomyType),
      spouseFullName: dto?.spouseFullName ?? null,
      spouseNationality: dto?.spouseNationality ?? null,
      spouseRfc: dto?.spouseRfc ?? null,
      spouseBirthPlace: dto?.spouseBirthPlace ?? null,
      spouseBirthDate: dto?.spouseBirthDate ?? null,
      spouseEmail: dto?.spouseEmail ?? null,
      spousePhone: dto?.spousePhone ?? null,
      spouseOccupation: toSelect(dto?.spouseOccupation),
      spouseMonthlyIncome: dto?.spouseMonthlyIncome ?? null
    };
  }

  private buildFormData(body: CustomerFormPayload): FormData {
    const form = new FormData();
    const append = (key: string, value: any) => {
      if (value == null || value === '') return;
      form.append(key, value);
    };

    append('typeUuid', body.typeUuid);
    append('classificationUuid', body.classificationUuid);
    append('genderUuid', body.genderUuid);
    append('identificationTypeUuid', body.identificationTypeUuid);
    append('identificationFileTypeUuid', body.identificationFileTypeUuid);
    append('companyTypeUuid', body.companyTypeUuid);
    append('curp', body.curp);
    append('rfc', body.rfc);
    append('firstName', body.firstName);
    append('middleName', body.middleName);
    append('lastName', body.lastName);
    append('secondLastName', body.secondLastName);
    append('businessName', body.businessName);
    append('identificationNumber', body.identificationNumber);
    append('cellphoneLada', body.cellphoneLada);
    append('cellphone', body.cellphone);
    append('phoneLada', body.phoneLada);
    append('phone', body.phone);
    append('email', body.email);
    append('birthDate', body.birthDate);
    append('employmentPlace', body.employmentPlace);
    append('socialSecurityNumber', body.socialSecurityNumber);
    append('notes', body.notes);
    append('clientCode', body.clientCode);

    if (body.avatar) form.append('avatar', body.avatar);
    if (body.identificationFront) form.append('identificationFront', body.identificationFront);
    if (body.identificationBack) form.append('identificationBack', body.identificationBack);
    if (body.fiscalCertificate) form.append('fiscalCertificate', body.fiscalCertificate);

    return form;
  }

  private appendMulti(params: HttpParams, key: string, values?: Array<string | number | null | undefined>): HttpParams {
    if (!values?.length) return params;
    return values.reduce((acc, value) => {
      if (value === null || value === undefined || value === '') return acc;
      return acc.append(key, String(value));
    }, params);
  }

  private buildCustomerListParams(
    page: number,
    size: number,
    keyword: string,
    sortBy: string,
    order: '' | 'asc' | 'desc',
    developments: string[],
    statuses: string[],
    types: string[],
    classifications: string[],
    identityStatusLabels: string[],
    columnFilters: Record<string, string>
  ): HttpParams {
    let params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size))
      .set('sortBy', sortBy)
      .set('order', order);

    if (keyword) params = params.set('keyword', keyword);
    params = this.appendMulti(params, 'developmentUuids', developments);
    params = this.appendMulti(params, 'statuses', statuses);
    params = this.appendMulti(params, 'types', types);
    params = this.appendMulti(params, 'classifications', classifications);
    params = this.appendMulti(params, 'identityStatusLabel', identityStatusLabels);
    return this.appendColumnFilters(params, columnFilters);
  }

  private appendColumnFilters(params: HttpParams, filters: Record<string, string>): HttpParams {
    const map: Record<string, string> = {
      nombre: 'qName',
      rfc: 'qRfc',
      curp: 'qCurp',
      tipo: 'qType',
      clasificacion: 'qClassification',
      celular: 'qCellphone',
      correo: 'qEmail',
      validacionIdentidad: 'qIdentityStatusLabel',
      estatus: 'qStatus'
    };

    return Object.entries(filters ?? {}).reduce((acc, [key, value]) => {
      const param = map[key];
      const normalized = value?.trim();
      return param && normalized ? acc.set(param, normalized) : acc;
    }, params);
  }

  private authHeaders(): HttpHeaders {
    const token = this.storage.get('access') ?? '';
    return new HttpHeaders().set('Authorization', `Bearer ${token}`);
  }

  private uuid(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return `${Math.random().toString(36).slice(2)}-${Date.now()}`;
  }
}
